import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';

import { AuditOperation, type AuditLogsFilterInput, type GetAuditLogsPaginatedQuery } from 'data/__generated__';

import { asyncMultiSelectColumn, dateRangeColumn, multiSelectColumn, textColumn } from 'components/DataTable';

import { getCreatorName, OPERATION_LABELS } from 'components/AuditLogs';
import { getAuditDisplay } from 'components/AuditLogs/audit-display.config';

export type AuditRow = GetAuditLogsPaginatedQuery['auditLogsPaginated']['auditLogs'][number];

export type AuditFilterOptions = {
    collections: string[];
    /** Cherche les utilisateurs côté serveur : ils se comptent en milliers, on n'en charge pas la liste. */
    loadAuthors: (search: string) => Promise<{ value: string; label: string }[]>;
};

/**
 * Les colonnes se construisent à l'exécution : les options du filtre « collection » viennent du
 * serveur, et celles du filtre « auteur » sont cherchées à la frappe. Activer une collection côté API
 * la rend filtrable sans toucher à ce fichier.
 */
export const buildAuditColumns = ({ collections, loadAuthors }: AuditFilterOptions): ColumnDef<AuditRow, unknown>[] => [
    dateRangeColumn<AuditRow, AuditLogsFilterInput>({
        accessorKey: 'createdAt',
        header: 'Date',
        filterKey: 'createdAt',
        defaultVisible: true,
        enableServerSort: true,
    }),
    asyncMultiSelectColumn<AuditRow, AuditLogsFilterInput>({
        accessorKey: 'createdBy',
        header: 'Auteur',
        filterKey: 'createdBy',
        defaultVisible: true,
        loadOptions: loadAuthors,
        cell: ({ row }) => getCreatorName(row.original.createdBy, row.original.createdByName),
    }),
    multiSelectColumn<AuditRow, AuditLogsFilterInput>({
        accessorKey: 'collectionName',
        header: 'Collection',
        filterKey: 'collectionName',
        defaultVisible: true,
        // Le nom physique, jamais traduit : c'est celui que porte la ligne d'audit, et celui qu'on
        // cherche quand on remonte une trace jusqu'à la base.
        options: collections
            .map((collectionName) => ({ value: collectionName, label: collectionName }))
            .sort((a, b) => a.label.localeCompare(b.label, 'fr')),
        cell: ({ row }) => row.original.collectionName,
    }),
    multiSelectColumn<AuditRow, AuditLogsFilterInput>({
        accessorKey: 'operation',
        header: 'Opération',
        filterKey: 'operation',
        defaultVisible: true,
        options: Object.values(AuditOperation).map((operation) => ({
            value: operation,
            label: OPERATION_LABELS[operation],
        })),
        cell: ({ row }) => OPERATION_LABELS[row.original.operation],
    }),
    textColumn<AuditRow, AuditLogsFilterInput>({
        accessorKey: 'collectionDocumentId',
        header: 'Id du document',
        filterKey: 'collectionDocumentId',
        defaultVisible: true,
        cell: ({ row }) => {
            const { collectionName, collectionDocumentId } = row.original;
            const detailPath = getAuditDisplay(collectionName)?.detailPath;

            // Les gabarits d'étagère et d'emplacement n'ont pas de page à eux : on montre l'identifiant
            // sans prétendre qu'il mène quelque part.
            if (!detailPath) {
                return collectionDocumentId;
            }

            return <Link to={`${detailPath}${collectionDocumentId}`}>{collectionDocumentId}</Link>;
        },
    }),
    textColumn<AuditRow, AuditLogsFilterInput>({
        id: 'changeCount',
        accessorFn: (row) => row.patches.length,
        header: 'Changements',
        defaultVisible: true,
    }),
    textColumn<AuditRow, AuditLogsFilterInput>({
        accessorKey: 'requestId',
        header: 'Identifiant d’action',
        filterKey: 'requestId',
        defaultVisible: false,
    }),
];
